import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useField } from "formik";
import { useRef } from "react";
import PTFormInput from "./PTFormInput";
import PTIcon from "./PTIcon";
import i18next from "i18next";
import PTTranslate from "./PTTranslate";
import { translate } from "../../utils/translate";

export const PTFileInput = ({
  name,
  label,
  required,
  isHidden,
  allowDownload = () => false,
  doDownload = () => {},
  xs = 12,
  md = 4,
  lg = 3,
  gridOptions,
  isDisabled = (inputName) => false,
  fileName = "",
  placeholder = "choose_file",
  multiple = false,
  ...props
}) => {
  const disabled = isDisabled(name);
  const [field, meta, helpers] = useField(name);
  const fileInputRef = useRef(null);

  const handleChange = (event) => {
    const newFiles = Array.from(event.target.files);
    
    if (multiple) {
      // If we already have files and we're in multiple mode, merge the files
      if (field.value && Array.isArray(field.value)) {
        // Create a map of existing file names to avoid duplicates
        const existingFileNames = new Map(
          field.value.map(file => [file.name, file])
        );
        
        // Add new files to the array, avoiding duplicates by name
        newFiles.forEach(file => {
          if (!existingFileNames.has(file.name)) {
            existingFileNames.set(file.name, file);
          }
        });
        
        // Convert the map values back to an array
        helpers.setValue(Array.from(existingFileNames.values()));
      } else {
        // If no existing files, just set the new files
        helpers.setValue(newFiles);
      }
    } else {
      // In single mode, replace with new file
      helpers.setValue(newFiles[0] || null);
    }
    
    if (props.onChange) props.onChange(event);
    
    // Reset file input to allow selecting the same file again
    fileInputRef.current.value = "";
  };

  const handleClick = () => {
    if (!disabled) fileInputRef.current.click();
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    e.preventDefault();
    doDownload();
  };

  const getTranslationString = (element) => {
    return i18next.t(element.props.tk);
  };

  // Display file names or the passed fileName prop
  const displayValue = () => {
    if (!field.value) return fileName;
    
    if (multiple && Array.isArray(field.value)) {
      return field.value
        .map((file) => file?.name)
        .filter(Boolean)
        .join(", ");
    }
    
    return field.value?.name || fileName;
  };

  return (
    <PTFormInput
      isHidden={isHidden}
      name={name}
      md={md}
      xs={xs}
      lg={lg}
      label={label}
      required={required}
      {...gridOptions}
      error={meta.touched && !!meta.error}
    >
      <input
        type="file"
        hidden
        onChange={handleChange}
        ref={fileInputRef}
        multiple={multiple}
        {...props}
      />
      <TextField
        fullWidth
        variant="outlined"
        value={displayValue()}
        onClick={handleClick}
        placeholder={getTranslationString(<PTTranslate tk={placeholder} />)}
        error={meta.touched && !!meta.error}
        helperText={meta.touched && translate(meta.error)}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          sx: {
            height: "40px",
            backgroundColor: disabled ? "#e0e0e0" : "#FFFFFF",
            cursor: disabled ? "not-allowed" : "pointer",
          },
          endAdornment: allowDownload() && (
            <InputAdornment position="end">
              <IconButton
                onClick={handleDownload}
                edge="end"
                sx={{ 
                  padding: '8px', 
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <PTIcon icon="download" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </PTFormInput>
  );
};

export default PTFileInput;